<script setup lang="ts">
import { computed, ref } from 'vue';
import ConfiguratorChevron from '../ConfiguratorChevron.vue';
import useWebsiteTexts from '@/composables/useWebsiteTexts';

const props = defineProps({
  stockData: {
    type: Object,
    required: true
  },
  quantity: {
    type: Number,
    default: 0
  },
  isGiftCard: {
    type: Boolean,
    default: false
  }
});

const options = computed(() =>
  props.stockData.filter((s) => s.type === 'option')
);

const { websiteText } = useWebsiteTexts();

const selectedStock = computed(() => {
  let total = 0;
  if (props.isGiftCard) {
    return {
      text: `${websiteText('productdetailspage_always_in_stock').value}`,
      icon: 'available'
    };
  }
  return (
    options.value.find((s) => {
      total += s.quantity;
      return props.quantity <= total;
    }) || options.value[0]
  );
});

const canOpenDropdown = computed(() => options.value.length > 1);

const dropdownOpen = ref(false);

function toggleDropdown() {
  dropdownOpen.value = !dropdownOpen.value;
}
</script>

<template>
  <div
    :class="[
      'quantity-selector-row-dropdown__stock',
      { 'quantity-selector-row-dropdown__stock--dropdown': canOpenDropdown },
      `quantity-selector-row-dropdown__stock-option--${selectedStock?.icon}`
    ]"
    @click="toggleDropdown"
  >
    {{ selectedStock.text }}
    <ConfiguratorChevron
      class="quantity-selector-row-dropdown__stock-chevron"
      v-if="canOpenDropdown"
      :open="dropdownOpen"
    >
    </ConfiguratorChevron>
    <div
      v-if="canOpenDropdown"
      :class="[
        'quantity-selector-row-dropdown__stock-options',
        { 'quantity-selector-row-dropdown__stock-options--open': dropdownOpen }
      ]"
    >
      <div
        v-for="stock in stockData"
        :class="[
          'quantity-selector-row-dropdown__stock-option',
          `quantity-selector-row-dropdown__stock-option--${stock.icon}`,
          {
            'quantity-selector-row-dropdown__stock-option--selected':
              stock.id === selectedStock?.id &&
              stock.text === selectedStock?.text
          },
          {
            'quantity-selector-row-dropdown__headline': stock.type == 'headline'
          }
        ]"
        :key="stock.id"
      >
        {{ stock.text }}
        <img
          v-if="
            stock.id === selectedStock?.id && stock.text === selectedStock?.text
          "
          class="quantity-selector-row-dropdown__stock-checkmark"
          src="@/static/icons/checkmark_icon.svg"
          alt="selected option checkmark"
        />
      </div>
    </div>
  </div>
</template>

<style>
.quantity-selector-row-dropdown__stock {
  display: flex;
  align-items: center;
  position: relative;
  padding: 3px 13px 3px 34px;
  font-size: 16px;
  line-height: 24px;
  border-radius: 2px;
  border: 1px solid var(--color-neutrals-50);
  height: 30px;
  width: 100%;
  user-select: none;
  background-color: var(--color-white);
}

.quantity-selector-row-dropdown__stock-option--out {
  background-color: var(--color-neutrals-10);
}

.quantity-selector-row-dropdown__stock--dropdown {
  cursor: pointer;
}

.quantity-selector-row-dropdown__stock-chevron,
.quantity-selector-row-dropdown__stock-checkmark {
  margin-left: auto;
}

.quantity-selector-row-dropdown__stock-options {
  position: absolute;
  top: calc(100% + 1px);
  left: -1px;
  width: calc(100% + 2px);
  background-color: var(--color-white);
  border: 1px solid var(--color-neutrals-50);
  border-top: none;
  z-index: 1;
  opacity: 0;
  max-height: 0px;
  transition: opacity 0.1s ease-in, max-height 0.2s ease-in;
  cursor: default;
}

.quantity-selector-row-dropdown__stock-options--open {
  opacity: 1;
  max-height: 300px;
}

.quantity-selector-row-dropdown__stock-option {
  position: relative;
  display: flex;
  height: 35px;
  font-size: 14px;
  line-height: 24px;
  align-items: center;
  padding: 0px 13px 0px 34px;
  display: none;
}

.quantity-selector-row-dropdown__headline {
  padding: 10px 12px 0px 12px;
}

.quantity-selector-row-dropdown__stock-options--open
  .quantity-selector-row-dropdown__stock-option {
  display: flex;
}

.quantity-selector-row-dropdown__stock-option--selected {
  background-color: rgba(83, 121, 255, 0.06);
}

.quantity-selector-row-dropdown__stock-option--out::before,
.quantity-selector-row-dropdown__stock-option--available::before,
.quantity-selector-row-dropdown__stock-option--incoming::before {
  position: absolute;
  content: '';
  border-radius: 50%;
  top: 8px;
  left: 13px;
  height: 14px;
  width: 14px;
}

.quantity-selector-row-dropdown__stock-options
  .quantity-selector-row-dropdown__stock-option--out::before,
.quantity-selector-row-dropdown__stock-options
  .quantity-selector-row-dropdown__stock-option--available::before,
.quantity-selector-row-dropdown__stock-options
  .quantity-selector-row-dropdown__stock-option--incoming::before {
  top: 11.5px;
  height: 12px;
  width: 12px;
}

.quantity-selector-row-dropdown__stock-option--out::before {
  background-color: var(--color-feedback-out-of-stock);
}

.quantity-selector-row-dropdown__stock-option--available::before {
  background-color: var(--color-feedback-in-stock);
}

.quantity-selector-row-dropdown__stock-option--incoming::before {
  background-color: var(--color-feedback-incoming-stock);
}
</style>
